import React, { useEffect, useRef, useState } from 'react';
import { getFetchRequest, postFormRequest, SITE_CONTENT_URL } from '../store/requests';
import { makeUrl } from '../constant';

const EMPTY_FORM = {
    footerAddress: '',
    footerEmail: '',
    footerPhone: '',
    clientHours: '',
    clientAddress: '',
    clientEmail: '',
    clientPhone: '',
    warehouseHours: '',
    warehouseAddress: '',
    warehouseEmail: '',
    warehousePhone: '',
    postalAddress: '',
    contactsEmail: '',
    website: '',
    mapLat: '',
    mapLng: '',
    hasPrivacyPolicy: false,
    hasPersonalData: false,
    hasSalesRules: false,
    privacyPolicyUrl: '',
    personalDataUrl: '',
    salesRulesUrl: '',
    invoicePhone: '',
    invoiceAddress: '',
    invoicePostalCode: '',
    invoiceKpp: '',
    invoiceInn: '',
    invoiceAccount: '',
    invoiceCorrAccount: '',
    invoiceBik: '',
    invoiceCompanyName: '',
};

const TEXT_FIELDS = [
    ['footerAddress', 'Адрес в подвале'],
    ['footerEmail', 'Почта в подвале'],
    ['footerPhone', 'Телефон в подвале'],
    ['clientHours', 'Клиентский отдел: режим работы'],
    ['clientAddress', 'Клиентский отдел: адрес'],
    ['clientEmail', 'Клиентский отдел: почта'],
    ['clientPhone', 'Клиентский отдел: телефон'],
    ['warehouseHours', 'Склад: режим работы'],
    ['warehouseAddress', 'Склад: адрес'],
    ['warehouseEmail', 'Склад: почта'],
    ['warehousePhone', 'Склад: телефон'],
    ['postalAddress', 'Почтовый адрес'],
    ['contactsEmail', 'E-mail на странице контактов'],
    ['website', 'Сайт'],
];

const INVOICE_FIELDS = [
    ['invoicePhone', 'Телефон'],
    ['invoiceAddress', 'Адрес'],
    ['invoicePostalCode', 'Почтовый индекс'],
    ['invoiceKpp', 'КПП'],
    ['invoiceInn', 'ИНН'],
    ['invoiceAccount', 'Р/с'],
    ['invoiceCorrAccount', 'К/с'],
    ['invoiceBik', 'БИК'],
    ['invoiceCompanyName', 'Название компании'],
];

const SiteInfoTab = ({ isCollapsed }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [privacyFile, setPrivacyFile] = useState(null);
    const [personalDataFile, setPersonalDataFile] = useState(null);
    const [salesRulesFile, setSalesRulesFile] = useState(null);
    const privacyInputRef = useRef(null);
    const personalDataInputRef = useRef(null);
    const salesRulesInputRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const applyContent = (data) => {
        setForm({
            ...EMPTY_FORM,
            ...data,
            mapLat: data?.mapLat ?? '',
            mapLng: data?.mapLng ?? '',
        });
    };

    useEffect(() => {
        getFetchRequest(
            makeUrl([SITE_CONTENT_URL]),
            (data) => {
                applyContent(data || {});
                setIsLoading(false);
            },
            (err) => {
                let message = 'Не удалось загрузить данные сайта';
                try {
                    const parsed = JSON.parse(err?.message || '');
                    if (parsed?.error) {
                        message = parsed.error;
                    }
                } catch (parseError) {
                    if (err?.message) {
                        message = err.message;
                    }
                }
                setError(message);
                setIsLoading(false);
            }
        );
    }, []);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        setIsSaving(true);
        setError('');
        const body = new FormData();
        TEXT_FIELDS.forEach(([field]) => {
            body.append(field, form[field] ?? '');
        });
        INVOICE_FIELDS.forEach(([field]) => {
            body.append(field, form[field] ?? '');
        });
        body.append('mapLat', form.mapLat ?? '');
        body.append('mapLng', form.mapLng ?? '');
        const privacy = privacyInputRef.current?.files?.[0] || privacyFile;
        const personalData = personalDataInputRef.current?.files?.[0] || personalDataFile;
        const salesRules = salesRulesInputRef.current?.files?.[0] || salesRulesFile;
        if (privacy) {
            body.append('privacyPolicy', privacy);
        }
        if (personalData) {
            body.append('personalData', personalData);
        }
        if (salesRules) {
            body.append('salesRules', salesRules);
        }
        postFormRequest(
            makeUrl([SITE_CONTENT_URL]),
            body,
            (data) => {
                applyContent(data || {});
                setPrivacyFile(null);
                setPersonalDataFile(null);
                setSalesRulesFile(null);
                if (privacyInputRef.current) privacyInputRef.current.value = '';
                if (personalDataInputRef.current) personalDataInputRef.current.value = '';
                if (salesRulesInputRef.current) salesRulesInputRef.current.value = '';
                setIsSaving(false);
                alert('Данные сохранены');
            },
            (err) => {
                console.error(err);
                setIsSaving(false);
                let message = 'Не удалось сохранить данные';
                try {
                    const parsed = JSON.parse(err?.message || '');
                    if (parsed?.error) {
                        message = parsed.error;
                    }
                } catch (parseError) {
                    if (err?.message) {
                        message = err.message;
                    }
                }
                setError(message);
            }
        );
    };

    const workspaceClass = isCollapsed === null
        ? ''
        : isCollapsed
            ? 'workspace_collapsed'
            : 'workspace_full-width';

    return (
        <section className={`workspace ${workspaceClass} calendar-settings__page`}>
            <h1 className="calendar-settings__title">Информация (документы и данные)</h1>
            {error && <div className="calendar-settings__error">{error}</div>}
            {isLoading ? (
                <div className="calendar-settings__loading">Загрузка...</div>
            ) : (
                <div className="site-info">
                    <section className="site-info__section">
                        <h2 className="site-info__title">Подвал сайта</h2>
                        {TEXT_FIELDS.slice(0, 3).map(([field, label]) => (
                            <label key={field} className="site-info__field">
                                <span>{label}</span>
                                <input
                                    type="text"
                                    value={form[field]}
                                    onChange={(e) => handleChange(field, e.target.value)}
                                />
                            </label>
                        ))}
                    </section>

                    <section className="site-info__section">
                        <h2 className="site-info__title">Страница контактов</h2>
                        {TEXT_FIELDS.slice(3).map(([field, label]) => (
                            <label key={field} className="site-info__field">
                                <span>{label}</span>
                                <input
                                    type="text"
                                    value={form[field]}
                                    onChange={(e) => handleChange(field, e.target.value)}
                                />
                            </label>
                        ))}
                        <div className="site-info__coords">
                            <label className="site-info__field">
                                <span>Широта</span>
                                <input
                                    type="text"
                                    value={form.mapLat}
                                    onChange={(e) => handleChange('mapLat', e.target.value)}
                                />
                            </label>
                            <label className="site-info__field">
                                <span>Долгота</span>
                                <input
                                    type="text"
                                    value={form.mapLng}
                                    onChange={(e) => handleChange('mapLng', e.target.value)}
                                />
                            </label>
                        </div>
                    </section>

                    <section className="site-info__section">
                        <h2 className="site-info__title">Документы</h2>
                        <label className="site-info__field">
                            <span>Политика конфиденциальности (PDF)</span>
                            <input
                                ref={privacyInputRef}
                                type="file"
                                accept="application/pdf,.pdf"
                                onChange={(e) => setPrivacyFile(e.target.files?.[0] || null)}
                            />
                            <small>
                                {privacyFile
                                    ? privacyFile.name
                                    : (form.hasPrivacyPolicy ? 'Файл уже загружен' : 'Файл ещё не загружен')}
                            </small>
                        </label>
                        <label className="site-info__field">
                            <span>Согласие на обработку персональных данных (PDF)</span>
                            <input
                                ref={personalDataInputRef}
                                type="file"
                                accept="application/pdf,.pdf"
                                onChange={(e) => setPersonalDataFile(e.target.files?.[0] || null)}
                            />
                            <small>
                                {personalDataFile
                                    ? personalDataFile.name
                                    : (form.hasPersonalData ? 'Файл уже загружен' : 'Файл ещё не загружен')}
                            </small>
                        </label>
                        <label className="site-info__field">
                            <span>Правила продажи товаров (PDF)</span>
                            <input
                                ref={salesRulesInputRef}
                                type="file"
                                accept="application/pdf,.pdf"
                                onChange={(e) => setSalesRulesFile(e.target.files?.[0] || null)}
                            />
                            <small>
                                {salesRulesFile
                                    ? salesRulesFile.name
                                    : (form.hasSalesRules ? 'Файл уже загружен' : 'Файл ещё не загружен')}
                            </small>
                        </label>
                    </section>

                    <section className="site-info__section">
                        <h2 className="site-info__title">PDF-счет</h2>
                        {INVOICE_FIELDS.map(([field, label]) => (
                            <label key={field} className="site-info__field">
                                <span>{label}</span>
                                <input
                                    type="text"
                                    value={form[field]}
                                    onChange={(e) => handleChange(field, e.target.value)}
                                />
                            </label>
                        ))}
                    </section>

                    <button
                        type="button"
                        className="order-button"
                        disabled={isSaving}
                        onClick={handleSave}
                    >
                        {isSaving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
            )}
        </section>
    );
};

export default SiteInfoTab;
